import type React from 'react';
import { memo } from 'react';

import type { Header } from '@tanstack/react-table';

import cn from '../../../utils/cn';
import { getCommonPinningStyles } from '../../DataTable/utils';

export interface TableHeadProps<TData>
  extends React.ComponentPropsWithoutRef<'th'> {
  className?: string;
  header?: Header<TData, unknown>;
  index?: number;
  alignHeader?: 'left' | 'center' | 'right';
  children: React.ReactNode;
}

const alignClasses = {
  right: 'justify-end text-right',
  center: 'm-auto table justify-center',
  left: '',
};

const TableHead = <TData,>({
  className,
  header,
  index,
  alignHeader,
  ...props
}: TableHeadProps<TData>) => {
  if (!header) return null;

  const alignClass = alignClasses[alignHeader || 'left'];

  return (
    <th
      style={{
        width:
          header.column.getSize() === Number.MAX_SAFE_INTEGER
            ? 'auto'
            : header.column.getSize() + 12,
        minWidth:
          header.column.getSize() === Number.MAX_SAFE_INTEGER
            ? 'auto'
            : header.column.getSize() + 12,
        ...getCommonPinningStyles<TData>(header.column),
      }}
      className={cn(
        'group align-middle',
        header?.column?.getIsResizing() && 'cursor-col-resize',
        className
      )}
      {...props}
    >
      <div
        className={cn('flex w-full items-center justify-between', alignClass)}
      >
        <div>{props.children}</div>
        <div className='w-3'>
          {header.column.getCanResize() ? (
            <div
              onMouseDown={header.getResizeHandler()}
              onTouchStart={header.getResizeHandler()}
              className={cn(
                'resizer mx-1 hidden h-5 w-1.5 animate-slide-left-and-fade cursor-col-resize rounded-md bg-gray-light group-hover:block',
                header.column.getIsResizing() ? 'isResizing block' : ''
              )}
            />
          ) : null}
        </div>
      </div>
    </th>
  );
};

TableHead.displayName = 'TableHead';

export default memo(TableHead);
